import ReactDOM from 'react-dom/client';
import { createBrowserRouter, RouterProvider, Outlet, useLocation, Link } from 'react-router-dom';
import Header from './components/Header/Header';
import Footer from './components/Footer/Footer';
import Main from './components/Main/Main';
import Error from './components/Error';
import ArrowIcon from './components/ArrowIcon/ArrowIcon';
import { Suspense, lazy, useEffect, useLayoutEffect, useRef, useState } from 'react';
import UserContext from './utils/userContext';
import { AccessibilityProvider } from './utils/AccessibilityContext';
import CookieBanner from './components/CookieBanner/CookieBanner';
import AccessibilityMenu from './components/AccessibilityMenu/AccessibilityMenu';
import { ROUTE_SLUGS, getLangFromPath, matchRouteKey, getRoutePaths, LANGUAGES, DEFAULT_LANG } from './utils/i18n';
import LanguageContext, { LanguageProvider, useLanguage } from './utils/LanguageContext';
import {useTranslations} from './utils/translations';
import ScrollToTop from './components/ScrollToTop';
import { serviceGroups, serviceGroupSlug, faqs as serviceFaqsKey} from './components/pages/Service/serviceData';
import prayritPortrait from 'url:../assets/prayrit.jpeg';
// import { faqSections } from './components/pages/Faq/faqData';
import 'bootstrap/dist/css/bootstrap.min.css';
import '../index.css';
import './responsive.css';

const About = lazy(() => import('./components/pages/About/About'));
// const Prayrit = lazy(() => import('./components/pages/Prayrit/Prayrit'));
const Contact = lazy(() => import('./components/pages/Contact/Contact'));
const Gallery = lazy(() => import('./components/pages/Gallery/Gallery'));
const Lab = lazy(() => import('./components/pages/Lab/Lab'));
const Service = lazy(() => import('./components/pages/Service/Service'));
// const Wall = lazy(() => import('./components/pages/Wall/Wall'));
// const Faq = lazy(() => import('./components/pages/Faq/Faq'));
// const Verse = lazy(() => import('./components/pages/Verse/Verse'));
const Principles = lazy(() => import('./components/pages/Principles/Principles'));
const Privacy = lazy(() => import('./components/pages/Privacy/Privacy'));
const Disclaimer = lazy(() => import('./components/pages/Disclaimer/Disclaimer'));
const Accessibility = lazy(() => import('./components/pages/Accessibility/Accessibility'));

const withRouteSuspense = (element) => (
    <Suspense fallback={null}>{element}</Suspense>
);

const SITE_TITLE = "thewhizkind";
const SITE_URL = "https://www.thewhizkind.com";
const DEFAULT_TITLE_TEMPLATE = '{title} | {site}';

const buildTitle = (meta, t) => {
    if (meta.titleKey) {
        const translated = t('common', meta.titleKey, '');
        if (typeof translated === 'string' && translated) {
            const translatedTemplate = meta.titleTemplateKey
                ? t('common', meta.titleTemplateKey, '')
                : '';
            const template =
                (typeof translatedTemplate === 'string' && translatedTemplate) ||
                meta.titleTemplate ||
                DEFAULT_TITLE_TEMPLATE;

            return template
                .replace('{title}', translated)
                .replace('{site}', SITE_TITLE);
        }
    }
    return meta.title || SITE_TITLE;
};

const DEFAULT_IMAGE = `${SITE_URL}/assets/seo/og-banner.jpg`;
const PRAYRIT_PORTRAIT_URL = new URL(prayritPortrait, window.location.origin).href;
const DEFAULT_KEYWORDS =
    "thewhizkind, independent creative and technology team, AI, digitalisation, process optimisation, web development, automation, branding, graphic design, business, Frankfurt am Main";
const ROUTE_LABELS = {
    home: "Home",
    gallery: "Gallery",
    lab: "The Lab",
    services: "Services",
    wall: "Wall",
    about: "About",
    faq: "FAQ",
    contact: "Contact",
    principles: "Our Principles",
    verse: "Verse",
    store: "Store",
    nyx: "Nyx",
    invoiceMaker: "Invoice Maker",
    privacy: "Privacy Policy",
    disclaimer: "Legal Disclaimer",
    accessibility: "Accessibility",
    notFound: "Not Found",
};
const DEFAULT_META = {
    title: SITE_TITLE,
    description:
        "thewhizkind is an independent creative and technology team based in Frankfurt am Main, working across design, web development, AI, automation and business.",
    image: DEFAULT_IMAGE,
    imageAlt: "thewhizkind main visual",
    imageWidth: "1200",
    imageHeight: "630",
    type: "website",
    schemaType: "WebPage",
    twitterCard: "summary_large_image",
    robots: "index, follow",
    keywords: DEFAULT_KEYWORDS,
};
const PAGE_META = {
    home: {
        ...DEFAULT_META,
        titleKey: 'pageMeta.title.home',
        titleTemplate: '{site} — {title}',
        datePublished: "2023-01-01",
        dateModified: "2026-09-14",
        keywords: "thewhizkind, independent creative and technology team, AI, digitalisation, process optimisation, web development, automation, graphic design, branding, business, Frankfurt am Main",
    },
    gallery: {
        titleKey: `pageMeta.title.gallery`,
        description:
            "A gallery of selected visual work by thewhizkind: branding, identity, graphic design, campaigns, editorial and visual communication for cultural institutions and partners across Germany and India.",
        image: DEFAULT_IMAGE,
        imageAlt: "thewhizkind main visual",
        schemaType: "CollectionPage",
        datePublished: "2023-01-01",
        dateModified: "2026-09-12",
        keywords: "thewhizkind gallery, visual portfolio, branding projects, visual identity work, graphic design, editorial design, Goethe-Institut design, cultural institution branding, visual communication",
    },
    lab: {
        titleKey: `pageMeta.title.lab`,
        description:
            "Inside thewhizkind's lab: argus, proba, fero, atlas, vex, nyx and sophos — AI, automation and workflow tools built around real problems for real teams.",
        image: DEFAULT_IMAGE,
        imageAlt: "thewhizkind main visual",
        schemaType: "CollectionPage",
        datePublished: "2026-08-15",
        dateModified: "2026-08-16",
        keywords: "thewhizkind lab, AI tools, automation, process automation, argus, proba, fero, atlas, sophos, vex, nyx, dashboards, custom software",
    },
    services: {
        titleKey: `pageMeta.title.services`,
        description:
            "thewhizkind's services span brand & design, web & digital, AI, automation & tools, and finance & business — based in Frankfurt am Main.",
        image: DEFAULT_IMAGE,
        imageAlt: "thewhizkind design and technology services",
        schemaType: "CollectionPage",
        datePublished: "2026-08-22",
        dateModified: "2026-09-14",
        keywords: "design services Frankfurt, web development, branding, AI automation, process optimisation, custom digital tools, finance business support, thewhizkind services",
    },
    wall: {
        title: `Wall — ${SITE_TITLE} | Visual Experiments & Posters`,
        description:
            "Discover visual experiments, posters, and illustrations from thewhizkind across branding, storytelling, and creative technology.",
        image: DEFAULT_IMAGE,
        imageAlt: "thewhizkind main visual",
        schemaType: "CollectionPage",
        datePublished: "2023-01-01",
        dateModified: "2026-01-01",
        keywords: "thewhizkind visual experiments, poster design, graphic illustration, experimental typography, creative posters Germany India, visual storytelling",
    },
    about: {
        titleKey: `pageMeta.title.about`,
        titleTemplateKey: 'pageMeta.template.about',
        description:
            "thewhizkind is an independent creative and technology team based in Frankfurt am Main — what we make, how we work, and the people behind it, including co-founder Prayrit Singh.",
        image: PRAYRIT_PORTRAIT_URL,
        imageAlt: "Portrait of Prayrit Singh, co-founder of thewhizkind, in Frankfurt am Main",
        imageWidth: "960",
        imageHeight: "1280",
        type: "website",
        schemaType: "AboutPage",
        datePublished: "2023-01-01",
        dateModified: "2026-09-14",
        keywords: "thewhizkind, about, Prayrit Singh, co-founder, design technology business, Frankfurt am Main",
    },
    faq: {
        title: `FAQ | ${SITE_TITLE} Creative & Tech Partners`,
        description:
            "Read answers to common questions about thewhizkind's branding, web development, process, pricing, and project collaboration.",
        image: DEFAULT_IMAGE,
        imageAlt: "thewhizkind main visual",
        schemaType: "WebPage",
        keywords: "thewhizkind FAQ, how much does a website cost, branding project timeline, custom website vs WordPress, React SEO, creative agency process India Germany",
    },
    contact: {
        titleKey: `pageMeta.title.contact`,
        description:
            "Get in touch with thewhizkind, based in Frankfurt am Main, for branding, web development, AI, automation and digital projects.",
        image: DEFAULT_IMAGE,
        imageAlt: "thewhizkind main visual",
        schemaType: "ContactPage",
        datePublished: "2023-01-01",
        dateModified: "2026-09-14",
        keywords: "contact thewhizkind, start a project, branding and web development Frankfurt, AI and automation partner, prayrit@thewhizkind.com",
    },
    principles: {
        titleKey: `pageMeta.title.principles`,
        description:
            "A few things thewhizkind believes in — about people, creativity, technology, and how we'd like to exist in the world.",
        image: DEFAULT_IMAGE,
        imageAlt: "thewhizkind main visual",
        schemaType: "WebPage",
        datePublished: "2026-08-16",
        dateModified: "2026-08-16",
        keywords: "thewhizkind principles, company values, independent creative and technology team culture, how we work, humans and AI, accessibility, kindness at work",
    },
    verse: {
        title: `Verse — ${SITE_TITLE} Products & Releases`,
        description:
            "Explore thewhizkind Verse, a growing home for products, releases, Store, and Nyx.",
        image: DEFAULT_IMAGE,
        imageAlt: "thewhizkind main visual",
        schemaType: "CollectionPage",
        datePublished: "2026-04-18",
        dateModified: "2026-04-18",
        keywords: "thewhizkind verse, thewhizkind products, Store, Nyx, creative products, product releases",
    },
    store: {
        title: `Store — ${SITE_TITLE}`,
        description:
            "Visit thewhizkind's store for products and releases.",
        image: DEFAULT_IMAGE,
        imageAlt: "thewhizkind main visual",
        robots: "noindex, nofollow",
        schemaType: "WebPage",
        keywords: DEFAULT_KEYWORDS,
    },
    nyx: {
        title: `Nyx — ${SITE_TITLE}`,
        description:
            "Visit Nyx by thewhizkind.",
        image: DEFAULT_IMAGE,
        imageAlt: "thewhizkind main visual",
        robots: "noindex, nofollow",
        schemaType: "WebPage",
        keywords: DEFAULT_KEYWORDS,
    },
    invoiceMaker: {
        title: `Invoice Maker — ${SITE_TITLE}`,
        description:
            "Clean, simple invoicing. No ads, no clutter, just a fast way to create professional invoices.",
        image: DEFAULT_IMAGE,
        imageAlt: "thewhizkind main visual",
        robots: "noindex, nofollow",
        schemaType: "WebPage",
        datePublished: "2026-05-07",
        dateModified: "2026-05-07",
        keywords: "Invoice Maker, simple invoice tool, professional invoices, lightweight invoice builder, thewhizkind Verse",
    },
    privacy: {
        titleKey: `pageMeta.title.privacy`,
        titleTemplate: '{title} — {site}',
        description: "Read how thewhizkind collects, uses, and protects personal data.",
        image: DEFAULT_IMAGE,
        imageAlt: "thewhizkind main visual",
        schemaType: "WebPage",
        datePublished: "2023-01-01",
        dateModified: "2025-12-01",
        keywords: DEFAULT_KEYWORDS,
    },
    disclaimer: {
        titleKey: `pageMeta.title.disclaimer`,
        titleTemplate: '{title} — {site}',
        description: "Read the legal disclaimer for thewhizkind.",
        image: DEFAULT_IMAGE,
        imageAlt: "thewhizkind main visual",
        schemaType: "WebPage",
        datePublished: "2023-01-01",
        dateModified: "2025-12-01",
        keywords: DEFAULT_KEYWORDS,
    },
    accessibility: {
        titleKey: `pageMeta.title.accessibility`,
        titleTemplate: '{title} — {site}',
        description: "How accessibility is built into thewhizkind, our WCAG 2.2 Level AA target, and the preferences available across the website.",
        image: DEFAULT_IMAGE,
        imageAlt: "thewhizkind main visual",
        schemaType: "WebPage",
        datePublished: "2026-08-20",
        dateModified: "2026-08-20",
        keywords: "thewhizkind accessibility, WCAG 2.2 Level AA, accessible web design, keyboard navigation, screen readers, reduced motion",
    },
    notFound: {
        title: `404 Not Found — ${SITE_TITLE}`,
        description: "The page you requested does not exist on thewhizkind.",
        image: DEFAULT_IMAGE,
        imageAlt: "thewhizkind main visual",
        robots: "noindex, nofollow",
        keywords: "",
        schemaType: "WebPage",
    },
};

const normalizeSeoText = (text) => (typeof text === "string" ? text.replace(/\s+/g, " ").trim() : "");
/* const FAQ_SCHEMA_ITEMS = faqSections.flatMap((section) =>
    (section.items || []).map((item) => {
        const answerParts = [
            ...(item.answer || []),
            ...(item.list ? [item.list.join(", ")] : []),
            ...(item.link?.label ? [item.link.label] : []),
        ]
            .map(normalizeSeoText)
            .filter(Boolean);
        return {
            "@type": "Question",
            name: normalizeSeoText(item.question),
            acceptedAnswer: {
                "@type": "Answer",
                text: answerParts.join(" "),
            },
        };
    })
); */

// Structured data for /services is derived directly from the visible copy in
// Service.js (serviceGroups/faqs) so the JSON-LD never drifts out of sync with
// what a visitor actually reads on the page.

const PERSON_SCHEMA = {
    "@context": "https://schema.org",
    "@type": "Person",
    "@id": `${SITE_URL}/#prayrit`,
    name: "Prayrit Singh",
    givenName: "Prayrit",
    familyName: "Singh",
    url: `${SITE_URL}/about`,
    image: {
        "@type": "ImageObject",
        "@id": `${SITE_URL}/about#portrait`,
        url: PRAYRIT_PORTRAIT_URL,
        contentUrl: PRAYRIT_PORTRAIT_URL,
        width: 960,
        height: 1280,
        caption: "Portrait of Prayrit Singh in Frankfurt am Main",
    },
    jobTitle: "Co-founder",
    worksFor: {
        "@type": "Organization",
        "@id": `${SITE_URL}/#organization`,
        name: SITE_TITLE,
    },
    homeLocation: {
        "@type": "Place",
        name: "Frankfurt am Main",
        address: {
            "@type": "PostalAddress",
            addressLocality: "Frankfurt am Main",
            addressCountry: "DE",
        },
    },
    sameAs: [
        "https://www.linkedin.com/in/prayrit-singh/",
        "https://www.instagram.com/thewhizkind",
    ],
    knowsLanguage: ["en", "de", "hi"],
    description: "Prayrit Singh is the co-founder of thewhizkind, based in Frankfurt am Main with roots in New Delhi, working across design, technology, AI, automation and business.",
};

const NotFound = () => {
    const location = useLocation();

    return (
        <section className="not-found-page" aria-labelledby="not-found-title">
            <p className="not-found-eyebrow">error / 404</p>
            <p className="not-found-code" aria-hidden="true">404</p>
            <h1 id="not-found-title" className="not-found-title">page not found</h1>
            <p className="not-found-copy">
                The page you requested is unavailable or no longer exists.
            </p>
            <p className="not-found-path">
                requested path <code>{location.pathname}</code>
            </p>
            <Link to="/" className="not-found-home-link">
                <ArrowIcon direction="left" /> back to main page
            </Link>
        </section>
    );
};

const AppLayout = () => {
    const [user, setUser] = useState("default");
    const [isScrolled, setIsScrolled] = useState(false);
    const [routeAnnouncement, setRouteAnnouncement] = useState("");
    const location = useLocation();
    const appRef = useRef(null);
    const isFirstRouteRef = useRef(true);
    const { currentLang } = useLanguage();
    const normalizedPath = location.pathname.replace(/\/+$/, "") || "/";
    const prevPathRef = useRef(normalizedPath);
    const pathLang = getLangFromPath(location.pathname);
    const {t} = useTranslations();
    const serviceFaqs = t("service", serviceFaqsKey, []);
    const lang = currentLang || pathLang;
    const matchedRouteKey = matchRouteKey(pathLang, location.pathname);
    const routeKey = matchedRouteKey || (normalizedPath === "/" ? "home" : "notFound");
    const isNotFoundPage = routeKey === "notFound";


    useLayoutEffect(() => {
        const headerEl = document.querySelector('.header-main');
        const footerEl = document.querySelector('.footer');
        if (!headerEl) return;
        const root = document.documentElement;
        const updateLayoutHeights = () => {
            const height = headerEl.getBoundingClientRect().height;
            if (height) {
                root.style.setProperty('--header-height', `${Math.round(height)}px`);
            }
            if (footerEl) {
                const footerHeight = footerEl.getBoundingClientRect().height;
                if (footerHeight) {
                    root.style.setProperty('--footer-height', `${Math.round(footerHeight)}px`);
                }
            }
        };
        updateLayoutHeights();
        let headerObserver;
        let footerObserver;
        if (typeof ResizeObserver !== 'undefined') {
            headerObserver = new ResizeObserver(updateLayoutHeights);
            headerObserver.observe(headerEl);
            if (footerEl) {
                footerObserver = new ResizeObserver(updateLayoutHeights);
                footerObserver.observe(footerEl);
            }
        }
        window.addEventListener('resize', updateLayoutHeights);
        return () => {
            window.removeEventListener('resize', updateLayoutHeights);
            if (headerObserver) headerObserver.disconnect();
            if (footerObserver) footerObserver.disconnect();
        };
    }, []);

    useEffect(() => {
        const setMeta = (key, value, isProperty = false) => {
            const selector = isProperty
                ? `meta[property="${key}"]`
                : `meta[name="${key}"]`;
            let tag = document.head.querySelector(selector);
            if (!tag) {
                tag = document.createElement('meta');
                tag.setAttribute(isProperty ? 'property' : 'name', key);
                document.head.appendChild(tag);
            }
            tag.setAttribute('content', value);
        };

        const removeMeta = (key, isProperty = false) => {
            const selector = isProperty
                ? `meta[property="${key}"]`
                : `meta[name="${key}"]`;
            const tag = document.head.querySelector(selector);
            if (tag) {
                tag.remove();
            }
        };

        const setCanonical = (href) => {
            let link = document.head.querySelector('link[rel="canonical"]');
            if (!link) {
                link = document.createElement('link');
                link.setAttribute('rel', 'canonical');
                document.head.appendChild(link);
            }
            link.setAttribute('href', href);
        };

        const setAlternate = (hreflang, href) => {
            let link = document.head.querySelector(`link[rel="alternate"][hreflang="${hreflang}"]`);
            if (!link) {
                link = document.createElement('link');
                link.setAttribute('rel', 'alternate');
                link.setAttribute('hreflang', hreflang);
                document.head.appendChild(link);
            }
            link.setAttribute('href', href);
        };

        const setJsonLd = (id, payload) => {
            let script = document.head.querySelector(`script[type="application/ld+json"][data-seo-id="${id}"]`);
            if (!script) {
                script = document.createElement('script');
                script.type = 'application/ld+json';
                script.setAttribute('data-seo-id', id);
                document.head.appendChild(script);
            }
            script.textContent = JSON.stringify(payload);
        };

        const removeJsonLd = (id) => {
            const script = document.head.querySelector(`script[type="application/ld+json"][data-seo-id="${id}"]`);
            if (script) {
                script.remove();
            }
        };

        const toTitleCase = (value) =>
            value
                .split(/[-_]/g)
                .filter(Boolean)
                .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
                .join(' ');

        const meta = { ...DEFAULT_META, ...(PAGE_META[routeKey] || {}) };
        const pageTitle = buildTitle(meta, t);
        const canonicalUrl =
            normalizedPath === "/" ? `${SITE_URL}/` : `${SITE_URL}${normalizedPath}`;


        // Get current language for meta tags
        const langForMeta = currentLang || lang || DEFAULT_LANG;
        document.title = pageTitle;
        setMeta('author', routeKey === 'about' ? 'Prayrit Singh' : SITE_TITLE);
        setMeta('description', meta.description);
        if (typeof meta.keywords === 'string' && meta.keywords.trim()) {
            setMeta('keywords', meta.keywords);
        } else {
            removeMeta('keywords');
        }
        setMeta('robots', meta.robots || DEFAULT_META.robots);
        setMeta('og:title', pageTitle, true);
        setMeta('og:description', meta.description, true);
        setMeta('og:url', canonicalUrl, true);
        setMeta('og:type', meta.type || DEFAULT_META.type, true);
        setMeta('og:site_name', SITE_TITLE, true);
        setMeta('og:locale', langForMeta === 'de' ? 'de_DE' : 'en_US', true);
        setMeta('og:image', meta.image, true);
        setMeta('og:image:secure_url', meta.image, true);
        setMeta('og:image:alt', meta.imageAlt, true);
        setMeta('og:image:width', meta.imageWidth || DEFAULT_META.imageWidth, true);
        setMeta('og:image:height', meta.imageHeight || DEFAULT_META.imageHeight, true);
        setMeta('og:image:type', 'image/jpeg', true);
        removeMeta('profile:first_name', true);
        removeMeta('profile:last_name', true);
        removeMeta('profile:username', true);
        setMeta('twitter:card', meta.twitterCard || DEFAULT_META.twitterCard);
        setMeta('twitter:site', '@thewhizkind');
        setMeta('twitter:creator', '@thewhizkind');
        setMeta('twitter:title', pageTitle);
        setMeta('twitter:description', meta.description);
        setMeta('twitter:image', meta.image);
        setMeta('twitter:image:alt', meta.imageAlt);
        setMeta('twitter:url', canonicalUrl);
        setCanonical(canonicalUrl);
        // hreflang: always derived from the path WITHOUT language prefix
        const basePath = normalizedPath.replace(/^\/(?:de|en)(?=\/|$)/, '') || '/';
        const enUrl = `${SITE_URL}${basePath}`;
        const deUrl = `${SITE_URL}/de${basePath === '/' ? '' : basePath}`;
        setAlternate('en', enUrl);
        setAlternate('de', deUrl);
        setAlternate('x-default', enUrl);

        const GEO_ROUTES = ['home', 'about', 'contact'];
        if (GEO_ROUTES.includes(routeKey)) {
            setMeta('geo.region', 'DE-HE');
            setMeta('geo.placename', 'Frankfurt am Main');
            setMeta('geo.position', '50.1109;8.6821');
            setMeta('ICBM', '50.1109, 8.6821');
        } else {
            removeMeta('geo.region');
            removeMeta('geo.placename');
            removeMeta('geo.position');
            removeMeta('ICBM');
        }

        const pageSchema = {
            "@context": "https://schema.org",
            "@type": meta.schemaType || DEFAULT_META.schemaType,
            "@id": `${canonicalUrl}#webpage`,
            name: pageTitle,
            description: meta.description,
            url: canonicalUrl,
            inLanguage: langForMeta === 'de' ? 'de' : 'en',
            isPartOf: { "@id": `${SITE_URL}/#website` },
            about: { "@id": `${SITE_URL}/#organization` },
            ...(meta.datePublished ? { datePublished: meta.datePublished } : {}),
            ...(meta.dateModified ? { dateModified: meta.dateModified } : {}),
        };
        if (routeKey === 'home') {
            pageSchema.primaryImageOfPage = {
                "@type": "ImageObject",
                url: DEFAULT_IMAGE,
                width: 1200,
                height: 630,
            };
        }
        if (routeKey === 'contact') {
            pageSchema.mainEntity = {
                "@type": "ContactPoint",
                contactType: "General inquiries",
                email: "prayrit@thewhizkind.com",
                availableLanguage: ["en"],
            };
        }
        if (routeKey === 'about') {
            // The page is primarily about the organisation (hero, philosophy, motto)
            // with one dedicated founder section — so `about`/mainEntity stay on the
            // Organization, and the Person is linked separately via route-person
            // below and Organization.founder in index.html, rather than claimed as
            // this page's mainEntity.
            pageSchema.primaryImageOfPage = { "@id": `${SITE_URL}/about#portrait` };
        }
        setJsonLd('route-page', pageSchema);

        /* if (routeKey === 'faq' && FAQ_SCHEMA_ITEMS.length) {
            setJsonLd('route-faq', {
                "@context": "https://schema.org",
                "@type": "FAQPage",
                mainEntity: FAQ_SCHEMA_ITEMS,
            });
        } else {
            removeJsonLd('route-faq');
        } */

        // Note: /gallery's own ItemList (with real project images and links) is
        // rendered inline by Gallery.js — no separate route-level copy here, to
        // avoid two competing ItemList graphs describing the same projects.

        if (routeKey === 'services') {
            setJsonLd('route-services', {
                "@context": "https://schema.org",
                "@type": "Service",
                "@id": `${SITE_URL}/services#service`,
                name: "Design, web development, AI, automation and business services",
                description: meta.description,
                url: `${SITE_URL}/services`,
                provider: { "@id": `${SITE_URL}/#organization` },
                areaServed: [
                    { "@type": "Country", name: "Germany" },
                    { "@type": "Country", name: "India" },
                    { "@type": "Place", name: "Worldwide" },
                ],
                hasOfferCatalog: {
                    "@type": "OfferCatalog",
                    name: "thewhizkind services",
                    itemListElement: serviceGroups.map((group) => ({
                        "@type": "OfferCatalog",
                        "@id": `${SITE_URL}/services#${serviceGroupSlug(group.title)}`,
                        name: group.title,
                        description: group.intro,
                        itemListElement: group.services.map(([name, description]) => ({
                            "@type": "Offer",
                            itemOffered: { "@type": "Service", name, description },
                        })),
                    })),
                },
            });
            setJsonLd('route-service-faq', {
                "@context": "https://schema.org",
                "@type": "FAQPage",
                "@id": `${SITE_URL}/services#faq`,
                mainEntity: serviceFaqs.map(([question, answer]) => ({
                    "@type": "Question",
                    name: normalizeSeoText(question),
                    acceptedAnswer: { "@type": "Answer", text: normalizeSeoText(answer) },
                })),
            });
        } else {
            removeJsonLd('route-services');
            removeJsonLd('route-service-faq');
        }

        if (routeKey === 'about') {
            setJsonLd('route-person', PERSON_SCHEMA);
        } else {
            removeJsonLd('route-person');
        }

        // Language prefix (e.g. 'de') is not a breadcrumb level of its own
        const allSegments = normalizedPath === "/" ? [] : normalizedPath.split('/').filter(Boolean);
        const crumbLangPrefix = LANGUAGES.includes(allSegments[0]) && allSegments[0] !== DEFAULT_LANG? `/${allSegments[0]}`: '';
        const pathSegments = crumbLangPrefix ? allSegments.slice(1) : allSegments;

        const breadcrumbItems = [
            {
                "@type": "ListItem",
                position: 1,
                name: ROUTE_LABELS.home,
                item: `${SITE_URL}${crumbLangPrefix || '/'}`,
            },
        ];

        pathSegments.forEach((segment, index) => {
            const partialPath = `${crumbLangPrefix}/${pathSegments.slice(0, index + 1).join('/')}`;
            const partialRouteKey = matchRouteKey(pathLang, partialPath);
            breadcrumbItems.push({
                "@type": "ListItem",
                position: index + 2,
                name: ROUTE_LABELS[partialRouteKey] || toTitleCase(segment),
                item: `${SITE_URL}${partialPath}`,
            });
        });

        if (breadcrumbItems.length > 1) {
            setJsonLd('route-breadcrumbs', {
                "@context": "https://schema.org",
                "@type": "BreadcrumbList",
                itemListElement: breadcrumbItems,
            });
        } else {
            removeJsonLd('route-breadcrumbs');
        }

        if (isFirstRouteRef.current) {
            isFirstRouteRef.current = false;
        } else {
            if (prevPathRef.current !== normalizedPath) appRef.current?.focus({ preventScroll: true });
            setRouteAnnouncement(pageTitle);
        }
        prevPathRef.current = normalizedPath;
    }, [normalizedPath, routeKey, currentLang]);

    return (
        <>
            <a href="#main-content" className="skip-link">Skip to main content</a>
            <div aria-live="polite" className="visually-hidden">{routeAnnouncement}</div>
            <CookieBanner />
            <AccessibilityMenu />
            <div className="background-wrapper">
            <Header scrolled={isScrolled} />
            <ScrollToTop />
            <main id="main-content" className={`app${isNotFoundPage ? " app-not-found" : ""}${routeKey === "verse" ? " app-verse" : ""}`} ref={appRef} tabIndex={-1}>
                <div className="page-load" key={`${location.pathname}${location.search}`}>
                    <Outlet />
                </div>
            </main>
            <Footer />
            </div>
        </>
    );
};

const StoreRedirect = () => {
    useEffect(() => {
        window.location.replace("https://www.thewhizstore.com/");
    }, []);
    return null;
};

const NyxRedirect = () => {
    useEffect(() => {
        window.location.replace("https://nyx.thewhizkind.com/");
    }, []);
    return null;
};

const InvoiceMakerRedirect = () => {
    useEffect(() => {
        window.location.replace("https://inv.thewhizkind.com/");
    }, []);
    return null;
};

const buildRoutes = () => ({
    path: "/",
    element: (
      <LanguageProvider>
        <AccessibilityProvider>
          <UserContext.Provider value={{ user: "default", setUser: () => {} }}>
            <AppLayout />
          </UserContext.Provider>
        </AccessibilityProvider>
      </LanguageProvider>
    ),
    children: [
        { index: true, element: <Main /> },
        
        // English routes (default, no prefix)
        { path: `/${ROUTE_SLUGS.gallery.en}`, element: withRouteSuspense(<Gallery />) },
        { path: `/${ROUTE_SLUGS.lab.en}`, element: withRouteSuspense(<Lab />) },
        { path: `/${ROUTE_SLUGS.services.en}`, element: withRouteSuspense(<Service />) },
        { path: `/${ROUTE_SLUGS.about.en}`, element: withRouteSuspense(<About />) },
        { path: `/${ROUTE_SLUGS.contact.en}`, element: withRouteSuspense(<Contact />) },
        { path: `/${ROUTE_SLUGS.principles.en}`, element: withRouteSuspense(<Principles />) },
        { path: `/${ROUTE_SLUGS.invoiceMaker.en}`, element: <InvoiceMakerRedirect /> },
        { path: `/${ROUTE_SLUGS.store.en}`, element: <StoreRedirect /> },
        { path: `/${ROUTE_SLUGS.nyx.en}`, element: <NyxRedirect /> },
        { path: `/${ROUTE_SLUGS.privacy.en}`, element: withRouteSuspense(<Privacy />) },
        { path: `/${ROUTE_SLUGS.disclaimer.en}`, element: withRouteSuspense(<Disclaimer />) },
        { path: `/${ROUTE_SLUGS.accessibility.en}`, element: withRouteSuspense(<Accessibility />) },
        
        // German routes (with /de prefix)
        { path: `/de/${ROUTE_SLUGS.gallery.de}`, element: withRouteSuspense(<Gallery />) },
        { path: `/de/${ROUTE_SLUGS.lab.de}`, element: withRouteSuspense(<Lab />) },
        { path: `/de/${ROUTE_SLUGS.services.de}`, element: withRouteSuspense(<Service />) },
        { path: `/de/${ROUTE_SLUGS.about.de}`, element: withRouteSuspense(<About />) },
        { path: `/de/${ROUTE_SLUGS.contact.de}`, element: withRouteSuspense(<Contact />) },
        { path: `/de/${ROUTE_SLUGS.principles.de}`, element: withRouteSuspense(<Principles />) },
        { path: `/de/${ROUTE_SLUGS.invoiceMaker.de}`, element: <InvoiceMakerRedirect /> },
        { path: `/de/${ROUTE_SLUGS.store.de}`, element: <StoreRedirect /> },
        { path: `/de/${ROUTE_SLUGS.nyx.de}`, element: <NyxRedirect /> },
        { path: `/de/${ROUTE_SLUGS.privacy.de}`, element: withRouteSuspense(<Privacy />) },
        { path: `/de/${ROUTE_SLUGS.disclaimer.de}`, element: withRouteSuspense(<Disclaimer />) },
        { path: `/de/${ROUTE_SLUGS.accessibility.de}`, element: withRouteSuspense(<Accessibility />) },
        
        // Root path for /de
        { path: "/de", element: <Main /> },
        
        { path: "*", element: <NotFound /> },
    ],
    errorElement: <Error />,
});

const appRouter = createBrowserRouter([buildRoutes()], {
  future: { v7_relativeSplatPath: true },
});

const AppWithProviders = () => (
  <RouterProvider router={appRouter} future={{ v7_startTransition: true }} />
);

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(<AppWithProviders />);
